/**
 * Stroke geometry — pure, so "does the brush do what I meant?" is a question a
 * unit test can answer instead of one you discover by painting.
 *
 * Two stages:
 *
 *  1. **Smooth** the raw pointer samples. Mouse input is jagged and touch input
 *     is noisy; a stabiliser that lags the cursor slightly is what makes a
 *     brush feel *controlled* rather than twitchy.
 *  2. **Interpolate stamps** along the smoothed path at a fixed spacing, so the
 *     result depends on the path taken, not on how many samples the browser
 *     happened to deliver. A 200-sample stroke and a 20-sample stroke of the
 *     same shape must look the same.
 */

export interface StrokePoint {
  x: number;
  y: number;
  /** 0..1 from the Pointer Events API; 1 assumed when absent. */
  pressure?: number;
}

export interface BrushSettings {
  /** Diameter in document pixels. */
  size: number;
  /** 0..1. 1 is a hard edge, 0 the softest falloff. */
  hardness: number;
  /** 0..1 final opacity of the whole stroke. */
  opacity: number;
  /** 0..1 stabiliser strength. 0 passes input through untouched. */
  smoothing: number;
  /** Stamp spacing as a fraction of size. */
  spacing?: number;
}

export interface Stamp {
  x: number;
  y: number;
  /** Multiplied into the dab's alpha; carries pressure. */
  alpha: number;
}

export const DEFAULT_SPACING = 0.15;

export const DEFAULT_BRUSH: BrushSettings = {
  size: 12,
  hardness: 0.8,
  opacity: 1,
  smoothing: 0.45,
  spacing: DEFAULT_SPACING,
};

const MIN_SPACING = 0.01;

/**
 * Exponential stabiliser: each output point moves a fraction of the way toward
 * the newest sample.
 *
 * `strength = 0` is an exact passthrough (important for the pencil, which must
 * hit every pixel it is told to), and the endpoint is always honoured exactly,
 * so a stroke never stops short of where the pointer stopped.
 */
export function smoothStroke(points: StrokePoint[], strength: number): StrokePoint[] {
  if (points.length === 0) return [];
  const clamped = Math.max(0, Math.min(0.95, strength));
  if (clamped === 0 || points.length === 1) return [...points];

  // A fixed lag would be invisible at 60 Hz and sluggish at 240 Hz, so the
  // follow factor is expressed per sample and floored to keep it responsive.
  const follow = Math.max(0.05, 1 - clamped);
  const output: StrokePoint[] = [points[0] as StrokePoint];
  let current = points[0] as StrokePoint;

  for (const point of points.slice(1)) {
    current = {
      x: current.x + (point.x - current.x) * follow,
      y: current.y + (point.y - current.y) * follow,
      pressure: blendPressure(current.pressure, point.pressure),
    };
    output.push(current);
  }

  // Land where the pointer actually stopped.
  const last = points[points.length - 1] as StrokePoint;
  const tail = output[output.length - 1];
  if (tail && (tail.x !== last.x || tail.y !== last.y)) {
    output.push(last);
  }
  return output;
}

function blendPressure(a?: number, b?: number): number | undefined {
  if (a === undefined || b === undefined) return b;
  return (a + b) / 2;
}

function distance(a: StrokePoint, b: StrokePoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Walk the path and emit stamps every `spacing × size` pixels.
 *
 * The first and last points always get a stamp, so a single click paints a dot
 * and a stroke always reaches its endpoint.
 */
export function strokeStamps(points: StrokePoint[], settings: BrushSettings): Stamp[] {
  if (points.length === 0) return [];
  const size = Math.max(1, settings.size);
  const spacing = Math.max(MIN_SPACING, settings.spacing ?? DEFAULT_SPACING) * size;
  const smoothed = smoothStroke(points, settings.smoothing);

  const stamps: Stamp[] = [];
  const push = (point: StrokePoint) =>
    stamps.push({ x: point.x, y: point.y, alpha: point.pressure ?? 1 });

  push(smoothed[0] as StrokePoint);
  if (smoothed.length === 1) return stamps;

  // Distance still owed before the next stamp. Carrying it across segments is
  // what keeps spacing uniform no matter how the samples were distributed.
  let untilNext = spacing;
  for (let index = 1; index < smoothed.length; index += 1) {
    const from = smoothed[index - 1] as StrokePoint;
    const to = smoothed[index] as StrokePoint;
    const segment = distance(from, to);
    if (segment === 0) continue;

    let travelled = 0;
    while (travelled + untilNext <= segment) {
      travelled += untilNext;
      const ratio = travelled / segment;
      push({
        x: from.x + (to.x - from.x) * ratio,
        y: from.y + (to.y - from.y) * ratio,
        pressure: (from.pressure ?? 1) + ((to.pressure ?? 1) - (from.pressure ?? 1)) * ratio,
      });
      untilNext = spacing;
    }
    untilNext -= segment - travelled;
  }

  const last = smoothed[smoothed.length - 1] as StrokePoint;
  const previous = stamps[stamps.length - 1];
  if (!previous || Math.hypot(last.x - previous.x, last.y - previous.y) > 0.001) push(last);
  return stamps;
}

/**
 * Total ink coverage of a stroke, in square document pixels.
 *
 * Used by tests and by the status read-out; a straight 100 px stroke with a
 * 10 px brush covers roughly 1000 px², and a smoothing change must not change
 * that materially.
 */
export function strokeCoverage(stamps: Stamp[], size: number): number {
  const radius = Math.max(1, size) / 2;
  return stamps.length * Math.PI * radius * radius;
}

/** Bounding box of a stroke, useful for dirty-rect work later. */
export function strokeBounds(stamps: Stamp[], size: number) {
  if (stamps.length === 0) return null;
  const radius = Math.max(1, size) / 2;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const stamp of stamps) {
    minX = Math.min(minX, stamp.x - radius);
    minY = Math.min(minY, stamp.y - radius);
    maxX = Math.max(maxX, stamp.x + radius);
    maxY = Math.max(maxY, stamp.y + radius);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
